import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { money } from "@/data/site";

const FREE_SHIP = 250;

function Drawer({ open, onClose, title, testid, children, footer }) {
  return (
    <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} data-testid={testid}>
      <div className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <div className={`absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-gold/25 bg-black shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
          <h3 className="font-serif text-xl text-cream">{title}</h3>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="text-cream/70 hover:text-gold"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="border-t border-white/10 px-6 py-5">{footer}</div>}
      </div>
    </div>
  );
}

export function CartDrawer() {
  const { cartOpen, setCartOpen, cart, setQty, removeFromCart, subtotal, moveCartToWishlist } = useApp();
  const [notice, setNotice] = React.useState(false);
  const [waitEmail, setWaitEmail] = React.useState("");
  const [joined, setJoined] = React.useState(false);
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  const onCheckout = () => {
    setNotice(true);
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    const email = waitEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email address"); return; }
    try {
      await api.post("/waitlist", { email });
      setJoined(true);
      toast.success("You're on the list — we'll be in touch at relaunch");
    } catch { toast.error("Could not join the waitlist. Please try again."); }
  };

  return (
    <Drawer open={cartOpen} onClose={() => setCartOpen(false)} title="Shopping Bag" testid="cart-drawer"
      footer={cart.length > 0 && (
        <div>
          <div className="mb-4 flex items-center justify-between text-sm">
            <span className="text-cream/70">Subtotal</span>
            <span className="font-mono text-lg text-gold" data-testid="cart-subtotal">{money(subtotal)}</span>
          </div>
          {notice ? (
            <div className="space-y-3" data-testid="maintenance-notice">
              <div className="border border-yellow-600/40 bg-yellow-600/10 p-3 text-xs leading-relaxed text-yellow-200">
                Our checkout is currently under maintenance and we're not accepting payments just yet. Save your selected pieces to your wishlist — we'll notify you the moment we go live.
              </div>
              <button data-testid="move-to-wishlist" onClick={moveCartToWishlist}
                className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">
                Move to Wishlist
              </button>
              {joined ? (
                <p className="text-center text-xs text-gold" data-testid="waitlist-joined">✦ You're on the relaunch list</p>
              ) : (
                <form onSubmit={joinWaitlist} className="space-y-2" data-testid="waitlist-form">
                  <p className="text-center text-[0.7rem] uppercase tracking-widest text-cream/50">Notify me when we relaunch</p>
                  <div className="flex gap-2">
                    <input type="email" value={waitEmail} onChange={(e) => setWaitEmail(e.target.value)} placeholder="you@email.com"
                      data-testid="waitlist-email" className="flex-1 border border-white/15 bg-black px-3 py-2.5 text-sm text-cream outline-none focus:border-gold" />
                    <button data-testid="waitlist-submit" className="border border-gold px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-gold transition hover:bg-gold hover:text-black">Join</button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <button data-testid="checkout-button" onClick={onCheckout}
              className="w-full bg-gold py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">
              Checkout
            </button>
          )}
        </div>
      )}>
      {cart.length === 0 ? (
        <EmptyState icon={<ShoppingBag size={32} />} title="Your bag is empty"
          text="Add pieces you love and they will appear here." />
      ) : (
        <>
          <div className="mb-6" data-testid="free-ship-progress">
            <p className="mb-2 text-xs text-cream/60">
              {remaining > 0 ? <>Add <span className="text-gold">{money(remaining)}</span> for complimentary shipping</> : <span className="text-gold">You've unlocked free shipping ✦</span>}
            </p>
            <div className="h-1 w-full bg-white/10"><div className="h-full bg-gold transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          </div>
          <div className="space-y-5">
            {cart.map((l) => (
              <div key={l.variant.id} className="flex gap-4" data-testid={`cart-line-${l.variant.id}`}>
                <img src={l.product.images?.[0]} alt={l.product.name} className="h-24 w-20 object-cover border border-white/10" />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className="text-sm text-cream">{l.product.name}</p>
                    <button onClick={() => removeFromCart(l.variant.id)} aria-label="Remove" data-testid={`cart-remove-${l.variant.id}`} className="text-cream/40 hover:text-gold"><X size={14} /></button>
                  </div>
                  <p className="mt-0.5 text-xs text-mutedgray">{l.variant.color} · {l.variant.size}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center border border-white/15">
                      <button onClick={() => setQty(l.variant.id, l.quantity - 1)} aria-label="Decrease" className="px-2 py-1 text-cream hover:text-gold"><Minus size={13} /></button>
                      <span className="w-8 text-center text-sm text-cream">{l.quantity}</span>
                      <button onClick={() => setQty(l.variant.id, l.quantity + 1)} aria-label="Increase" className="px-2 py-1 text-cream hover:text-gold"><Plus size={13} /></button>
                    </div>
                    <span className="font-mono text-sm text-gold">{money(l.product.price * l.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}

export function WishlistDrawer() {
  const { wishOpen, setWishOpen, wishlist, toggleWishlist, addToCart } = useApp();
  return (
    <Drawer open={wishOpen} onClose={() => setWishOpen(false)} title="Wishlist" testid="wishlist-drawer">
      {wishlist.length === 0 ? (
        <EmptyState icon={<span className="text-3xl">♡</span>} title="No saved pieces yet"
          text="Tap the heart on any product to save it for later." />
      ) : (
        <div className="space-y-5">
          {wishlist.map((p) => (
            <div key={p.id} className="flex gap-4" data-testid={`wishlist-line-${p.slug}`}>
              <Link to={`/product/${p.slug}`} onClick={() => setWishOpen(false)}>
                <img src={p.images?.[0]} alt={p.name} className="h-24 w-20 object-cover border border-white/10" />
              </Link>
              <div className="flex flex-1 flex-col justify-between">
                <div>
                  <Link to={`/product/${p.slug}`} onClick={() => setWishOpen(false)} className="text-sm text-cream hover:text-gold">{p.name}</Link>
                  <p className="mt-1 font-mono text-sm text-gold">{money(p.price)}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { addToCart(p, p.variants?.[0], 1); }} className="text-xs uppercase tracking-widest text-gold hover:text-gold-bright" data-testid={`wishlist-add-${p.slug}`}>Add to bag</button>
                  <button onClick={() => toggleWishlist(p)} className="text-xs uppercase tracking-widest text-cream/40 hover:text-cream">Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function EmptyState({ icon, title, text }) {
  return (
    <div className="flex h-full flex-col items-center justify-center py-20 text-center">
      <div className="mb-5 text-gold/50">{icon}</div>
      <p className="font-serif text-lg text-cream">{title}</p>
      <p className="mt-2 max-w-xs text-sm text-cream/50">{text}</p>
    </div>
  );
}
